import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "@/hooks/use-toast";
import { Link } from "wouter";

const requestResetSchema = z.object({
  email: z.string().email("Invalid email address"),
});

const confirmResetSchema = z
  .object({
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export function ResetPasswordForm({ token }: { token?: string }) {
  const [isLoading, setIsLoading] = useState(false);

  const requestForm = useForm({
    resolver: zodResolver(requestResetSchema),
    defaultValues: { email: "" },
  });

  const confirmForm = useForm({
    resolver: zodResolver(confirmResetSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const onRequestReset = async (data: z.infer<typeof requestResetSchema>) => {
    try {
      setIsLoading(true);
      const response = await fetch("/api/reset-password/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || "Failed to request password reset");
      }

      toast({
        title: "Success",
        description: "Check your email for reset instructions",
      });
    } catch (error) {
      const isNetworkError = error instanceof TypeError;
      toast({
        title: "Error",
        description: isNetworkError
          ? "Unable to connect. Please check your internet connection and try again."
          : (error instanceof Error ? error.message : "Failed to request password reset"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onConfirmReset = async (data: z.infer<typeof confirmResetSchema>) => {
    try {
      setIsLoading(true);
      const response = await fetch("/api/reset-password/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: data.password }),
      });

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.error || "Failed to reset password");
      }

      toast({
        title: "Success",
        description: "Password reset successful",
      });

      window.location.href = "/";
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reset password",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (token) {
    return (
      <div className="w-full">
        <form onSubmit={confirmForm.handleSubmit(onConfirmReset)}>
          <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden">
            <div className="relative">
              <input
                type="password"
                placeholder="New password"
                className="w-full bg-transparent px-4 py-3.5 text-[18px] text-white placeholder:text-white/30 outline-none"
                {...confirmForm.register("password")}
              />
            </div>
            <div className="border-t border-white/[0.06]" />
            <div className="relative">
              <input
                type="password"
                placeholder="Confirm password"
                className="w-full bg-transparent px-4 py-3.5 text-[18px] text-white placeholder:text-white/30 outline-none"
                {...confirmForm.register("confirmPassword")}
              />
            </div>
          </div>

          {Object.keys(confirmForm.formState.errors).length > 0 && (
            <div className="mt-2 px-1">
              {Object.values(confirmForm.formState.errors).map((error, i) => (
                <p key={i} className="text-[14px] text-red-400">{error?.message as string}</p>
              ))}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full mt-6 py-3 rounded-xl bg-white text-black text-[18px] font-medium hover:bg-white/90 active:bg-white/80 transition-colors disabled:opacity-40"
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        <p className="text-center mt-6 text-[14px] text-white/40">
          <Link href="/login">
            <a className="text-white/70 hover:text-white transition-colors">Back to Sign In</a>
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <form onSubmit={requestForm.handleSubmit(onRequestReset)}>
        <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden">
          <div className="relative">
            <input
              type="email"
              placeholder="Email"
              className="w-full bg-transparent px-4 py-3.5 text-[18px] text-white placeholder:text-white/30 outline-none"
              {...requestForm.register("email")}
            />
          </div>
        </div>

        {requestForm.formState.errors.email && (
          <div className="mt-2 px-1">
            <p className="text-[14px] text-red-400">{requestForm.formState.errors.email.message}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full mt-6 py-3 rounded-xl bg-white text-black text-[18px] font-medium hover:bg-white/90 active:bg-white/80 transition-colors disabled:opacity-40"
        >
          {isLoading ? "Sending..." : "Send Reset Link"}
        </button>
      </form>

      <p className="text-center mt-6 text-[14px] text-white/40">
        <Link href="/login">
          <a className="text-white/70 hover:text-white transition-colors">Back to Sign In</a>
        </Link>
      </p>
    </div>
  );
}
